import { differenceInCalendarDays, format, isToday, isYesterday, isValid, parse } from "date-fns";

import type { AnalyticsData, Recording } from "../../api/types";
import { formatDuration, parseUtcDate } from "../../utils";
import { processInstances } from "./utils";

export const relativeDay = (date: Date) => {
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "MMM dd");
};

// Below this, a session is short enough that where it falls in the day is the
// distinguishing fact about it.
const MORNING_AFTERNOON_THRESHOLD_HOURS = 4;
// Below this, a session spans most of a working day but isn't yet long enough
// that counting the calendar days it touches says more than "it was a long one".
const DAILY_THRESHOLD_HOURS = 10;

/**
 * The deterministic session title: a duration/day-part descriptor computed in
 * the viewer's local time zone, replacing the former "processA & processB"
 * heuristic (which read badly for a half-day session with several distinct
 * activities, and produced "X & X" when the same process showed up twice).
 */
const sessionDurationTitle = (start: Date, end: Date): string => {
  const hours = (end.getTime() - start.getTime()) / 3_600_000;
  if (hours <= MORNING_AFTERNOON_THRESHOLD_HOURS) {
    return start.getHours() < 12 ? "Morning Session" : "Afternoon Session";
  }
  if (hours <= DAILY_THRESHOLD_HOURS) {
    return "Daily Session";
  }
  // Calendar days touched, not duration / 24h rounded up: a 10h30 session that
  // starts at 00:30 and never crosses midnight is still "1-Day Session".
  const days = differenceInCalendarDays(end, start) + 1;
  return `${days}-Day Session`;
};

export const buildSessionTitle = (
  details: AnalyticsData | undefined,
  itemId: string,
  recording?: Pick<Recording, "date_started" | "date_ended" | "name">,
) => {
  if (recording?.name?.trim()) {
    return recording.name.trim();
  }
  if (details) {
    return sessionDurationTitle(
      new Date(Number(details.startTimestamp) * 1000),
      new Date(Number(details.endTimestamp) * 1000),
    );
  }
  if (recording) {
    return sessionDurationTitle(
      parseUtcDate(recording.date_started),
      parseUtcDate(recording.date_ended ?? recording.date_started),
    );
  }
  const parsed = parse(itemId, "yyyyMMdd_HHmm", new Date());
  if (isValid(parsed)) return `Work Session`;
  return `Session ${itemId}`;
};

// "Jul 08, 00:19-10:04" inside one day; once the span crosses midnight the end gets its own day
// ("Jul 08, 00:19 - Jul 09, 10:04") so the reader can tell a 10-hour recording from a 34-hour one.
const formatDayTimeRange = (start: Date, end: Date): string => {
  const startLabel = `${relativeDay(start)}, ${format(start, "HH:mm")}`;
  if (start.toDateString() === end.toDateString()) {
    return `${startLabel}-${format(end, "HH:mm")}`;
  }
  return `${startLabel} - ${relativeDay(end)}, ${format(end, "HH:mm")}`;
};

export const buildSubtitle = (
  details: AnalyticsData | undefined,
  itemId: string,
  recording?: Pick<Recording, "date_started" | "date_ended">,
) => {
  if (details) {
    const start = new Date(Number(details.startTimestamp) * 1000);
    const end = new Date(Number(details.endTimestamp) * 1000);
    const duration = formatDuration(Number(details.durationSeconds));
    const processCount = processInstances(details).length;
    return `${formatDayTimeRange(start, end)} - ${duration} - ${processCount} processes`;
  }
  if (recording) {
    const start = parseUtcDate(recording.date_started);
    const end = parseUtcDate(recording.date_ended ?? recording.date_started);
    return formatDayTimeRange(start, end);
  }
  const parsed = parse(itemId, "yyyyMMdd_HHmm", new Date());
  if (isValid(parsed)) {
    return `${relativeDay(parsed)}, ${format(parsed, "HH:mm")}`;
  }
  return itemId;
};

/** Match the session header's start time and observed duration. */
export const buildSessionContextDetails = (
  details?: Pick<AnalyticsData, "startTimestamp" | "durationSeconds"> | null,
  recording?: Pick<Recording, "date_started" | "date_ended"> | null,
  mappedSeconds?: number,
): string => {
  if (!details && !recording) return "";
  const start = details
    ? new Date(Number(details.startTimestamp) * 1000)
    : parseUtcDate(recording!.date_started);
  const duration =
    mappedSeconds ??
    (details
      ? Number(details.durationSeconds)
      : recording?.date_ended
        ? (parseUtcDate(recording.date_ended).getTime() - start.getTime()) / 1000
        : null);
  return `${format(start, "d MMM yyyy, HH:mm")}${duration !== null ? ` (${formatDuration(duration)})` : ""}`;
};
